import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Cpu,
  Square,
  Rows,
  LayoutGrid,
  Expand,
  Shrink,
  Columns,
  Globe,
  Flag,
  Settings,
  LogIn,
  Sun,
  MoonStar,
  Bell,
  User,
  Command,
  Search as SearchIcon,
} from "lucide-react";
import M2TLogo from "../common/M2TLogo";

function IconBtn({ children, tip, onClick }) {
  return (
    <div className="group relative flex items-center justify-center">
      <button
        className="flex h-[20px] w-[20px] items-center justify-center rounded text-black transition hover:bg-white"
        onClick={onClick}
        tabIndex={0}
        type="button"
      >
        {children}
      </button>

      {/* Tooltip BELOW icon */}
      {tip && (
        <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-[4px] -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-1.5 py-0.5 text-[7px] text-white opacity-0 shadow-lg transition-all group-hover:translate-y-[2px] group-hover:opacity-100">
          {tip}
        </span>
      )}
    </div>
  );
}

export default function TopBar({
  initialMode = "ai",
  onModeChange,
  splitMode,
  setSplitMode,
  showAppBar,
  setShowAppBar,
  orientation,
  setOrientation,
  fitScreen,
  setFitScreen,
  theme = "dark",
  onThemeToggle,
  onLogin,
  onSettings,
  onLanguage,
  onCountry,
  onProfile,
  onCommand,
}) {
  const [mode, setMode] = useState(initialMode || "ai");
  const ThemeIcon = theme === "dark" ? Sun : MoonStar;

  function handleModeSwitch() {
    const next = mode === "ai" ? "standard" : "ai";
    setMode(next);
    if (onModeChange) onModeChange(next);
  }

  return (
    <div
      className="fixed left-0 top-0 z-50 flex w-full items-center justify-between border-b border-slate-800 bg-gradient-to-r from-slate-950 via-white/80 to-white px-4"
      style={{ height: "32px" }}
    >
      {/* --- Left: Logo + Mode --- */}
      <div className="flex flex-shrink-0 items-center gap-4">
        {/* <Link to="/" title="Go to Home">
          <M2TLogo className="w-6 h-6 cursor-pointer hover:opacity-80 transition" />
        </Link> */}
        <span className="text-[8px] font-bold tracking-tight text-yellow-400">
          M2T 1.0
        </span>

        <div className="ml-1 flex items-center">
          <span className="mr-1 text-[8px] font-semibold text-white/60">
            Mode:
          </span>
          <button
            className={`flex items-center justify-center gap-[2px] rounded-full border px-3 py-[1px] transition-all ${
              mode === "ai"
                ? "border-purple-400 bg-purple-900/20 hover:bg-purple-900/40"
                : "border-amber-400 bg-amber-900/20 hover:bg-amber-900/40"
            } `}
            onClick={handleModeSwitch}
            title={
              mode === "ai" ? "Switch to Standard Mode" : "Switch to AI Mode"
            }
          >
            {mode === "ai" ? (
              <>
                <span className="flex h-3.5 w-3.5 items-center justify-center overflow-hidden rounded-full border border-blue-400 bg-gradient-to-b from-gray-900 to-blue-900">
                  <img
                    src="/assets/images/Trilli-chat.jpg"
                    alt="Trilli AI"
                    className="h-full w-full rounded-full object-cover"
                    draggable={false}
                  />
                </span>
                <span className="relative top-[0.2px] text-[9px] font-bold leading-[16px] text-purple-300">
                  AI
                </span>
              </>
            ) : (
              <>
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-yellow-400 text-[8px] font-bold leading-[8px] text-slate-900">
                  S
                </span>
                <span className="relative top-[0.2px] text-[9px] font-bold leading-[10px] text-amber-300">
                  Std
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* --- Center: Search (Perfect Center Alignment) --- */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="relative -mt-0.5 w-[400px]">
          <SearchIcon className="absolute left-3 top-[55%] h-[11px] w-[11px] -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-md border border-white/50 bg-[#181B1E]/80 py-[4px] pl-8 pr-2 text-[10px] text-white placeholder-slate-400 shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
            placeholder="Search..."
          />
        </div>
      </div>

      {/* --- Right: Compact Icons --- */}
      <div className="flex flex-shrink-0 items-center gap-[2px] rounded-xl bg-white">
        <IconBtn tip="AppBar" onClick={() => setShowAppBar((v) => !v)}>
          <Cpu className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Single" onClick={() => setSplitMode(1)}>
          <Square className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Dual" onClick={() => setSplitMode(2)}>
          <Rows className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Quad" onClick={() => setSplitMode(4)}>
          <LayoutGrid className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn
          tip="Orient"
          onClick={() =>
            setOrientation((o) =>
              o === "vertical" ? "horizontal" : "vertical",
            )
          }
        >
          {orientation === "vertical" ? (
            <Columns className="h-[10px] w-[10px]" />
          ) : (
            <Rows className="h-[10px] w-[10px]" />
          )}
        </IconBtn>
        <IconBtn
          tip={fitScreen ? "Expand" : "Fit"}
          onClick={() => setFitScreen((v) => !v)}
        >
          {fitScreen ? (
            <Expand className="h-[10px] w-[10px]" />
          ) : (
            <Shrink className="h-[10px] w-[10px]" />
          )}
        </IconBtn>
        <IconBtn tip="Lang" onClick={onLanguage}>
          <Globe className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Country" onClick={onCountry}>
          <Flag className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Settings" onClick={onSettings}>
          <Settings className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Login" onClick={onLogin}>
          <LogIn className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn
          tip={theme === "dark" ? "Light" : "Dark"}
          onClick={onThemeToggle}
        >
          <ThemeIcon className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Notif">
          <div className="relative">
            <Bell className="h-[10px] w-[10px]" />
            <span className="absolute right-0 top-0 h-[4px] w-[4px] rounded-full border border-[#181B1E] bg-amber-400" />
          </div>
        </IconBtn>
        <IconBtn tip="Profile" onClick={onProfile}>
          <User className="h-[10px] w-[10px]" />
        </IconBtn>
        <IconBtn tip="Cmd" onClick={onCommand}>
          <Command className="h-[10px] w-[10px]" />
        </IconBtn>
      </div>
    </div>
  );
}
